import * as React from 'react';
import { cn } from '../../../lib/utils';

// ─── Size & visual-variant maps ───────────────────────────────────────────────

const sizeH = { sm: 'h-7', md: 'h-9', lg: 'h-10' } as const;
const sizeText = {
  sm: 'text-base sm:text-xs',
  md: 'text-base sm:text-sm',
  lg: 'text-base',
} as const;
const sizePx = { sm: 'px-2', md: 'px-3', lg: 'px-4' } as const;
const sizePy = { sm: 'py-1', md: 'py-2', lg: 'py-2' } as const;

const fieldVariantCls = {
  outline: 'border border-input bg-input-bg shadow-sm',
  filled: 'border border-transparent bg-muted',
  ghost: 'border border-transparent bg-transparent',
} as const;

// ─── Props ────────────────────────────────────────────────────────────────────

export interface InputAddonProps extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'size'> {
  /**
   * Content for the left add-on slot (typically a short text string like
   * `"https://"` or `"$"`).
   */
  start?: React.ReactNode;

  /**
   * Content for the right add-on slot (e.g. `".com"`, `"kg"`).
   */
  end?: React.ReactNode;

  /**
   * Layout variant:
   * - `'inline'`  — start/end sit **inside** a single shared border.
   *                 Focus ring appears on the outer wrapper.
   * - `'segment'` — start/end are **separate bordered segments** attached to
   *                 the input (classic "input group" prepend/append pattern).
   */
  variant?: 'inline' | 'segment';

  /**
   * Visual style of the field (mirrors `Input` prop).
   * - `'outline'` (default) — border + background
   * - `'filled'`  — muted background, no visible border
   * - `'ghost'`   — no border, no background
   */
  fieldVariant?: 'outline' | 'filled' | 'ghost';

  /** Field height / text size. Defaults to `'md'`. */
  size?: 'sm' | 'md' | 'lg';

  /** Additional CSS classes on the outer wrapper `<div>`. */
  className?: string;
}

// ─── Component ────────────────────────────────────────────────────────────────

/**
 * Extends `Input` with prepended / appended text add-ons in two layout modes.
 *
 * **inline** — start/end live inside a single shared border. The wrapper
 * receives the focus ring so it lights up as one cohesive field.
 *
 * **segment** — start/end are separate bordered tiles (muted background)
 * flanking the input. Classic "input group" pattern.
 *
 * Forwards its ref to the inner `<input>` element.
 *
 * @example
 * // Inline text add-on (shared border)
 * <InputAddon variant="inline" start="https://" end=".com">
 *   <input placeholder="shadcnstudio" /> {/* or use as self-closing *\/}
 * </InputAddon>
 *
 * // Segment add-on (separate bordered tiles)
 * <InputAddon variant="segment" start="https://" end=".com"
 *             placeholder="shadcnstudio" />
 */
const InputAddon = React.forwardRef<HTMLInputElement, InputAddonProps>(
  (
    {
      start,
      end,
      variant = 'inline',
      fieldVariant = 'outline',
      size = 'md',
      className,
      disabled,
      readOnly,
      ...inputProps
    },
    ref,
  ) => {
    const hasStart = start !== undefined && start !== null && start !== '';
    const hasEnd = end !== undefined && end !== null && end !== '';

    // ── Inline variant ────────────────────────────────────────────────────────
    if (variant === 'inline') {
      return (
        <div
          className={cn(
            // Outer wrapper carries border, bg, radius, focus ring
            'flex w-full overflow-hidden',
            sizeH[size],
            'rounded-[var(--field-radius,var(--radius))]',
            fieldVariantCls[fieldVariant],
            'focus-within:outline-none focus-within:ring-2 focus-within:ring-ring',
            'focus-within:ring-offset-2 focus-within:ring-offset-background',
            disabled && 'cursor-not-allowed opacity-50',
            className,
          )}
        >
          {hasStart && (
            <span
              className={cn(
                'flex shrink-0 select-none items-center pl-3 text-muted-foreground pointer-events-none',
                sizeText[size],
              )}
            >
              {start}
            </span>
          )}

          <input
            ref={ref}
            disabled={disabled}
            readOnly={readOnly}
            className={cn(
              // No border or bg — inherited from wrapper
              'flex min-w-0 flex-1 bg-transparent text-foreground',
              'placeholder:text-muted-foreground',
              'focus:outline-none',
              'disabled:cursor-not-allowed',
              'read-only:cursor-default',
              sizeText[size],
              sizePx[size],
              sizePy[size],
            )}
            {...inputProps}
          />

          {hasEnd && (
            <span
              className={cn(
                'flex shrink-0 select-none items-center pr-3 text-muted-foreground pointer-events-none',
                sizeText[size],
              )}
            >
              {end}
            </span>
          )}
        </div>
      );
    }

    // ── Segment variant ───────────────────────────────────────────────────────
    // Dynamic Tailwind arbitrary classes don't survive JIT scanning, so radius
    // is applied via inline styles instead.
    const cssRadius = 'var(--field-radius, var(--radius))';

    return (
      <div className={cn('flex w-full', className)}>
        {hasStart && (
          <span
            style={{ borderTopLeftRadius: cssRadius, borderBottomLeftRadius: cssRadius }}
            className={cn(
              'flex shrink-0 items-center bg-muted text-muted-foreground',
              'border border-r-0 border-input shadow-sm',
              sizeH[size],
              sizePx[size],
              sizeText[size],
            )}
          >
            {start}
          </span>
        )}

        <input
          ref={ref}
          disabled={disabled}
          readOnly={readOnly}
          style={{
            borderTopLeftRadius: hasStart ? 0 : cssRadius,
            borderBottomLeftRadius: hasStart ? 0 : cssRadius,
            borderTopRightRadius: hasEnd ? 0 : cssRadius,
            borderBottomRightRadius: hasEnd ? 0 : cssRadius,
          }}
          className={cn(
            'flex min-w-0 flex-1 text-foreground',
            fieldVariantCls[fieldVariant],
            'placeholder:text-muted-foreground',
            'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
            'focus-visible:ring-offset-2 focus-visible:ring-offset-background',
            'disabled:cursor-not-allowed disabled:opacity-50',
            'read-only:bg-muted read-only:cursor-default',
            'aria-[invalid=true]:border-destructive aria-[invalid=true]:focus-visible:ring-destructive',
            sizeH[size],
            sizePx[size],
            sizePy[size],
            sizeText[size],
          )}
          {...inputProps}
        />

        {hasEnd && (
          <span
            style={{ borderTopRightRadius: cssRadius, borderBottomRightRadius: cssRadius }}
            className={cn(
              'flex shrink-0 items-center bg-muted text-muted-foreground',
              'border border-l-0 border-input shadow-sm',
              sizeH[size],
              sizePx[size],
              sizeText[size],
            )}
          >
            {end}
          </span>
        )}
      </div>
    );
  },
);
InputAddon.displayName = 'InputAddon';

// ─── Exports ──────────────────────────────────────────────────────────────────

export { InputAddon };
